/*
 * MAW SFX definitions (docs/07 §2 juice table, §4.2).
 *
 * Every one-shot is one or more ZzFX parameter arrays ("layers") mixed into a single
 * mono buffer. audio.ts builds the buffers lazily (and caches them), plays them through
 * the SFX bus and applies per-play pitch jitter + round-robin variants.
 *
 * ZzFX positional params (v1.3.2):
 *   0 volume, 1 randomness, 2 frequency, 3 attack, 4 sustain, 5 release, 6 shape
 *   (0 sin, 1 tri, 2 saw, 3 tan, 4 noise, 5 square), 7 shapeCurve, 8 slide (≈ ×500 Hz/s),
 *   9 deltaSlide, 10 pitchJump (Hz), 11 pitchJumpTime, 12 repeatTime, 13 noise,
 *   14 modulation, 15 bitCrush, 16 delay, 17 sustainVolume, 18 decay, 19 tremolo,
 *   20 filter (NEGATIVE = lowpass, POSITIVE = highpass; cutoff ≈ 2 × |value| Hz — use lp()/hp()).
 * Index 1 (randomness) is always written explicitly: ZzFX defaults it to 0.05, which
 * would detune tonal sequences. For noisy/percussive sounds it makes each round-robin
 * variant slightly different.
 *
 * Mix guide: every layer is written at ~full scale; `gain` sets the mix level. Keep
 * tonal / frequently repeated sounds quiet — the tone is lounge, not arcade-shrill.
 */
import type { SfxName } from './audio';

export type ZzfxParams = readonly (number | undefined)[];

export interface ZzfxApi {
  volume: number;
  sampleRate: number;
  buildSamples(...params: (number | undefined)[]): number[];
}

export interface SfxLayer {
  p: ZzfxParams;
  /** Start offset in seconds inside the mixed buffer. */
  at?: number;
}

export interface SfxDef {
  layers: readonly SfxLayer[];
  /** Mix level into the bus (0..1). */
  gain: number;
  /** Round-robin variants (default 3). Variants differ in timbre (release/attack) and, for layers with randomness > 0, pitch. */
  variants?: number;
  /** Random playback-rate spread per play, as a ± fraction (default 0.05 = ±5%). Tonal sounds whose pitch the caller drives use ~0.5%. */
  jitter?: number;
  /** 'blip' = the voice-blip sub-bus (dot-matrix typing). */
  bus?: 'sfx' | 'blip';
  /** Max simultaneous voices of this sound; the oldest is cut (default 4). */
  maxVoices?: number;
  /** Retriggers closer than this (s) are dropped (default 0). */
  minGap?: number;
}

/** Filter helpers: ZzFX's filter cutoff is 2 × |value|. */
const lp = (hz: number): number => -hz / 2;
const hp = (hz: number): number => hz / 2;
/** MIDI note → Hz. */
const hz = (m: number): number => 440 * 2 ** ((m - 69) / 12);

// Small building blocks ------------------------------------------------------------

/** Short filtered noise transient (clicks, contact noise). */
const tick = (vol: number, freq: number, len: number, hpHz: number, rnd = 0.08): ZzfxParams =>
  [vol, rnd, freq, 0, len * 0.25, len * 0.75, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, hp(hpHz)];

/** Vibraphone-ish mallet note (sine + slow tremolo). */
const vibe = (m: number, vol: number, rel: number): ZzfxParams =>
  [vol, 0, hz(m), 0.004, 0.03, rel, 0, 1, 0, 0, 0, 0, 0.16, 0, 0, 0, 0, 1, 0, 0.22, 0];

/** Soft muted brass (lowpassed saw, slight attack). */
const brass = (m: number, vol: number, sus: number, rel: number, lpHz = 1800): ZzfxParams =>
  [vol, 0, hz(m), 0.025, sus, rel, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.75, 0.05, 0, lp(lpHz)];

/** Coin tinkle (two-step pitch jump up). */
const coinNote = (m: number, vol: number): ZzfxParams =>
  [vol, 0.02, hz(m), 0, 0.012, 0.13, 0, 1, 0, 0, hz(m + 5) - hz(m), 0.03, 0, 0, 0, 0, 0, 1, 0, 0, 0];

/** Bell partial (long release sine). */
const bell = (freq: number, vol: number, rel: number): ZzfxParams =>
  [vol, 0, freq, 0.002, 0.01, rel, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0.02, 0, 0];

// Deterministic "coin fountain" for gorge: pentatonic pitches, staggered.
const FOUNTAIN_NOTES = [86, 90, 88, 93, 91, 95, 88, 98, 93, 91, 96, 100];
const fountain: SfxLayer[] = FOUNTAIN_NOTES.map((m, i) => ({
  p: coinNote(m, 0.45 - i * 0.02),
  at: 0.5 + i * 0.065 + (i % 3) * 0.012,
}));

// The table ------------------------------------------------------------------------

export const SFX: Record<SfxName, SfxDef> = {
  // Lever ---------------------------------------------------------------------------
  /** Leather creak: low saw, AM-flutter at ~55 Hz, noisy, dark. */
  leverGrab: {
    gain: 0.2,
    layers: [{ p: [1, 0.08, 82, 0.03, 0.1, 0.1, 2, 1, 0.08, 0, 0, 0, 0.018, 0.8, 0, 0, 0, 0.8, 0.02, 0.7, lp(1400)] }],
  },
  /** One ratchet click (the caller fires it repeatedly while dragging). */
  ratchet: {
    gain: 0.15,
    maxVoices: 3,
    minGap: 0.025,
    layers: [
      { p: tick(1, 900, 0.022, 1600, 0.12) },
      { p: [0.6, 0.1, 310, 0, 0.002, 0.016, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
    ],
  },
  /** Mechanical release: bright contact noise + wooden body with a quick pitch drop. */
  clack: {
    gain: 0.24,
    layers: [
      { p: tick(1, 1800, 0.035, 1000) },
      { p: [1, 0.05, 190, 0, 0.015, 0.07, 1, 1.6, -1.5, 0, 0, 0, 0, 0.3, 0, 0, 0, 1, 0, 0, lp(2400)] },
    ],
  },
  /** Brighter clack + a two-note chime (G6, D7). */
  perfect: {
    gain: 0.28,
    variants: 2,
    jitter: 0.015,
    layers: [
      { p: tick(1, 2400, 0.03, 2000) },
      { p: [0.8, 0.03, 230, 0, 0.012, 0.06, 1, 1.4, -1.5, 0, 0, 0, 0, 0.2, 0, 0, 0, 1, 0, 0, lp(3000)] },
      { p: bell(hz(91), 0.55, 0.6), at: 0.02 },
      { p: bell(hz(98), 0.35, 0.55), at: 0.07 },
    ],
  },

  // Reels ---------------------------------------------------------------------------
  /** Thunk: low triangle with pitch drop + a small click. Caller adds +1 semitone per reel. */
  reelStop: {
    gain: 0.32,
    jitter: 0.03,
    maxVoices: 5,
    layers: [
      { p: [1, 0.04, 95, 0, 0.012, 0.1, 1, 1.4, -1, 0, 0, 0, 0, 0.4, 0, 0, 0, 1, 0, 0, lp(1800)] },
      { p: tick(0.5, 1200, 0.02, 800) },
    ],
  },
  /** Soft wind chime: four high sines, staggered (E6 G6 A6 D7). */
  fate: {
    gain: 0.12,
    jitter: 0.02,
    layers: [
      { p: bell(hz(88), 0.6, 0.7), at: 0 },
      { p: bell(hz(91), 0.5, 0.7), at: 0.06 },
      { p: bell(hz(93), 0.45, 0.65), at: 0.13 },
      { p: bell(hz(98), 0.35, 0.6), at: 0.21 },
    ],
  },
  /** Rising tension (one-shot ~0.75 s; the looping version is startLoop('drone')). */
  anticipation: {
    gain: 0.18,
    variants: 2,
    jitter: 0.02,
    layers: [
      { p: [1, 0, 98, 0.3, 0.25, 0.2, 2, 1, 0.25, 0, 0, 0, 0.06, 0, 0, 0, 0, 0.8, 0, 0.35, lp(1200)] },
      { p: [0.6, 0, 49, 0.3, 0.25, 0.2, 0, 1, 0.12, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
    ],
  },

  // Scoring -------------------------------------------------------------------------
  /** Coin blip B5→E6 (triangle, soft). Caller raises pitch per pattern. */
  patternBlip: {
    gain: 0.15,
    jitter: 0.005,
    maxVoices: 4,
    layers: [{ p: [1, 0, hz(83), 0, 0.035, 0.12, 1, 1, 0, 0, hz(88) - hz(83), 0.04, 0, 0, 0, 0, 0, 1, 0, 0, 0] }],
  },
  /** Soft tick at C5. Caller passes pentatonic semitone steps. */
  countTick: {
    gain: 0.13,
    jitter: 0.005,
    maxVoices: 6,
    minGap: 0.02,
    layers: [{ p: [1, 0, hz(72), 0, 0.008, 0.06, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] }],
  },
  /** "Ka" (noise click) … "chunk" (low thud + short metallic ring). */
  kachunk: {
    gain: 0.32,
    layers: [
      { p: tick(1, 1500, 0.03, 700) },
      { p: [1, 0.05, 120, 0, 0.02, 0.12, 1, 2, -1, 0, 0, 0, 0, 0.5, 0, 0, 0, 1, 0, 0, lp(1000)], at: 0.07 },
      { p: [0.3, 0.03, 620, 0, 0.01, 0.15, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.07 },
    ],
  },
  /** Coin tinkle (caller randomizes ±3 semitones). */
  coin: {
    gain: 0.11,
    maxVoices: 6,
    minGap: 0.015,
    layers: [
      { p: coinNote(95, 1) },
      { p: bell(hz(107), 0.25, 0.09) },
    ],
  },

  // Big wins ------------------------------------------------------------------------
  /** Bass hit + muted brass stab on Dm7. */
  bigHit: {
    gain: 0.26,
    variants: 2,
    jitter: 0.01,
    layers: [
      { p: [1, 0, 65, 0, 0.05, 0.5, 0, 1, -0.1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0.03, 0, 0] },
      { p: [0.6, 0.05, 800, 0, 0.01, 0.08, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(3000)] },
      { p: brass(62, 0.4, 0.12, 0.18), at: 0.01 },
      { p: brass(65, 0.35, 0.12, 0.18), at: 0.01 },
      { p: brass(69, 0.35, 0.12, 0.18), at: 0.012 },
      { p: brass(72, 0.3, 0.12, 0.18), at: 0.014 },
    ],
  },
  /** Music swell: slow crescendo of a D power chord (~1.4 s). */
  swell: {
    gain: 0.2,
    variants: 1,
    jitter: 0.005,
    layers: [
      { p: [0.5, 0, hz(50), 1.1, 0.1, 0.4, 2, 1, 0, 0, 0, 0, 0.08, 0, 0, 0, 0, 1, 0, 0.15, lp(1800)] },
      { p: [0.4, 0, hz(57), 1.1, 0.1, 0.4, 2, 1, 0, 0, 0, 0, 0.08, 0, 0, 0, 0, 1, 0, 0.15, lp(1800)] },
      { p: [0.35, 0, hz(62), 1.1, 0.1, 0.4, 1, 1, 0, 0, 0, 0, 0.08, 0, 0, 0, 0, 1, 0, 0.15, 0] },
    ],
  },
  /** Creepy little "ha-ha-ha": three descending, bit-crushed vocal-ish pulses. */
  laugh: {
    gain: 0.16,
    variants: 2,
    layers: [0, 1, 2].map((i) => ({
      at: i * 0.16,
      p: [1, 0.04, 260 - i * 22, 0.01, 0.06, 0.07, 2, 1, -0.3, 0, 0, 0, 0.012, 0.6, 0, 0.04, 0, 1, 0, 0.3, lp(1600)] as ZzfxParams,
    })),
  },
  /** Short jazzy vibes lick in D minor, landing on Dm9. */
  jingle: {
    gain: 0.17,
    variants: 1,
    jitter: 0,
    maxVoices: 2,
    layers: [
      { p: vibe(69, 0.7, 0.3), at: 0 },
      { p: vibe(72, 0.7, 0.3), at: 0.19 },
      { p: vibe(74, 0.7, 0.3), at: 0.29 },
      { p: vibe(77, 0.7, 0.3), at: 0.48 },
      { p: vibe(76, 0.65, 0.3), at: 0.58 },
      { p: vibe(74, 0.6, 0.3), at: 0.77 },
      { p: vibe(62, 0.4, 0.9), at: 0.96 },
      { p: vibe(65, 0.4, 0.9), at: 0.96 },
      { p: vibe(69, 0.4, 0.9), at: 0.97 },
      { p: vibe(76, 0.35, 0.9), at: 0.98 },
    ],
  },
  /** Gorge: big gulp, then a coin fountain. */
  gorge: {
    gain: 0.28,
    variants: 1,
    jitter: 0.02,
    maxVoices: 1,
    layers: [
      { p: [1, 0, 220, 0.02, 0.08, 0.2, 0, 1, -0.6, 0, 0, 0, 0, 0.4, 0, 0, 0, 1, 0, 0, lp(1000)] },
      { p: [1, 0, 70, 0.01, 0.2, 0.3, 1, 1, -0.1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
      ...fountain,
    ],
  },

  // Misses & symbol effects ---------------------------------------------------------
  /** "Ooh": low boop sliding down, slight tremolo. */
  nearMiss: {
    gain: 0.2,
    layers: [{ p: [1, 0.03, 330, 0.06, 0.12, 0.22, 1, 1, -0.25, 0, 0, 0, 0.07, 0, 0, 0, 0, 1, 0, 0.2, lp(1000)] }],
  },
  crunch: {
    gain: 0.2,
    layers: [{ p: [1, 0.1, 500, 0, 0.03, 0.08, 4, 1, 0, 0, 0, 0, 0.025, 1, 0, 0.2, 0, 1, 0, 0.5, lp(3000)] }],
  },
  /** Three quick high sines (E7 G7 B7). */
  sparkle: {
    gain: 0.09,
    layers: [
      { p: bell(hz(100), 0.6, 0.12), at: 0 },
      { p: bell(hz(103), 0.5, 0.12), at: 0.04 },
      { p: bell(hz(107), 0.4, 0.14), at: 0.08 },
    ],
  },
  pop: {
    gain: 0.18,
    layers: [{ p: [1, 0.08, 320, 0, 0.01, 0.05, 0, 1, 12, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] }],
  },
  /** Loud chomp: growly downward saw + teeth clack. */
  chomp: {
    gain: 0.3,
    maxVoices: 2,
    layers: [
      { p: [1, 0.05, 140, 0, 0.03, 0.14, 2, 1, -1.5, 0, 0, 0, 0, 0.8, 0, 0.15, 0, 1, 0, 0, lp(2000)] },
      { p: tick(1, 1600, 0.045, 900), at: 0.08 },
    ],
  },
  /** Rubber stamp: sub thud + paper slap. */
  stamp: {
    gain: 0.28,
    layers: [
      { p: [1, 0.03, 75, 0, 0.03, 0.15, 0, 1, -0.4, 0, 0, 0, 0, 0.2, 0, 0, 0, 1, 0, 0, 0] },
      { p: [0.8, 0.05, 900, 0, 0.005, 0.05, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(2400)] },
    ],
  },

  // Tribute / meta ------------------------------------------------------------------
  /** Dot-matrix printer zip: stepped square buzz, twice. */
  printer: {
    gain: 0.08,
    variants: 2,
    layers: [
      { p: [1, 0.03, 1100, 0.01, 0.17, 0.02, 5, 1, 0.5, 0, 0, 0, 0.011, 2, 0, 0, 0, 1, 0, 0.9, lp(3200)], at: 0 },
      { p: [1, 0.03, 1250, 0.01, 0.1, 0.02, 5, 1, 0.5, 0, 0, 0, 0.011, 2, 0, 0, 0, 1, 0, 0.9, lp(3200)], at: 0.24 },
    ],
  },
  /** Cash register: drawer clunk, then a bell ding (C7 + inharmonic partial). */
  register: {
    gain: 0.18,
    variants: 2,
    jitter: 0.02,
    layers: [
      { p: [0.8, 0.05, 200, 0, 0.02, 0.06, 1, 1, -1, 0, 0, 0, 0, 0.3, 0, 0, 0, 1, 0, 0, lp(1600)] },
      { p: bell(hz(96), 1, 0.9), at: 0.08 },
      { p: bell(hz(96) * 2.76, 0.2, 0.4), at: 0.08 },
    ],
  },
  /** Low swallowing rumble (~2.9 s). */
  rumble: {
    gain: 0.24,
    variants: 1,
    jitter: 0.02,
    maxVoices: 1,
    layers: [
      { p: [1, 0.02, 48, 0.3, 1.4, 1.2, 2, 1, -0.02, 0, 0, 0, 0.25, 0.5, 0, 0, 0, 1, 0, 0.4, lp(240)] },
      { p: [1, 0, 36, 0.4, 1.4, 1.2, 0, 1, -0.01, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
    ],
  },
  /** Choir "ah" on A4 (+E5): detuned lowpassed saws. Caller pitches by branch. */
  choir: {
    gain: 0.11,
    variants: 2,
    jitter: 0.005,
    layers: [
      { p: [0.6, 0.006, hz(69), 0.18, 0.25, 0.6, 2, 1, 0, 0, 0, 0, 0.19, 0, 0, 0, 0, 1, 0, 0.12, lp(1100)] },
      { p: [0.6, 0.006, hz(69), 0.2, 0.25, 0.6, 2, 1, 0, 0, 0, 0, 0.21, 0, 0, 0, 0, 1, 0, 0.12, lp(1100)] },
      { p: [0.5, 0.006, hz(57), 0.2, 0.25, 0.6, 2, 1, 0, 0, 0, 0, 0.23, 0, 0, 0, 0, 1, 0, 0.1, lp(900)] },
      { p: [0.35, 0.006, hz(76), 0.22, 0.25, 0.55, 1, 1, 0, 0, 0, 0, 0.17, 0, 0, 0, 0, 1, 0, 0.1, 0] },
    ],
  },

  // UI --------------------------------------------------------------------------
  /** Dot-matrix typing blip (~30/s). Very short, very quiet, on the voice-blip bus. */
  blip: {
    gain: 0.032,
    bus: 'blip',
    maxVoices: 3,
    minGap: 0.018,
    layers: [{ p: [1, 0.04, 700, 0, 0.012, 0.012, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(2400)] }],
  },
  click: {
    gain: 0.13,
    maxVoices: 3,
    layers: [
      { p: [1, 0.05, 1000, 0, 0.004, 0.015, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
      { p: tick(0.4, 2000, 0.01, 2000) },
    ],
  },
  hover: {
    gain: 0.05,
    maxVoices: 2,
    minGap: 0.04,
    layers: [{ p: [1, 0.05, 1600, 0, 0.002, 0.012, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] }],
  },
  /** Two soft low buzzes. */
  error: {
    gain: 0.15,
    maxVoices: 2,
    minGap: 0.1,
    layers: [
      { p: [1, 0.02, 140, 0, 0.07, 0.03, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(1400)], at: 0 },
      { p: [1, 0.02, 125, 0, 0.07, 0.04, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(1400)], at: 0.11 },
    ],
  },

  // Bonus / scratch -------------------------------------------------------------
  /** One scratch-off grain. */
  scratch: {
    gain: 0.08,
    jitter: 0.08,
    maxVoices: 4,
    minGap: 0.03,
    layers: [{ p: [1, 0.15, 2400, 0, 0.02, 0.03, 4, 1, 0, 0, 0, 0, 0, 1.5, 0, 0, 0, 1, 0, 0, hp(1200)] }],
  },
  /** Rising arpeggio D6 F6 A6 D7 + a sparkle on top. */
  scratchWin: {
    gain: 0.14,
    variants: 1,
    jitter: 0.005,
    layers: [
      { p: [1, 0, hz(86), 0, 0.03, 0.15, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0 },
      { p: [1, 0, hz(89), 0, 0.03, 0.15, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.06 },
      { p: [1, 0, hz(93), 0, 0.03, 0.15, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.12 },
      { p: [1, 0, hz(98), 0, 0.05, 0.3, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.18 },
      { p: bell(hz(105), 0.3, 0.2), at: 0.22 },
    ],
  },
  /** Dull descending two notes (A4 → F4) + dust. */
  scratchVoid: {
    gain: 0.15,
    variants: 2,
    jitter: 0.01,
    layers: [
      { p: [1, 0, hz(69), 0, 0.06, 0.12, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(1600)], at: 0 },
      { p: [1, 0, hz(65), 0, 0.08, 0.2, 1, 1, -0.05, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(1600)], at: 0.14 },
      { p: [0.3, 0.1, 600, 0, 0.05, 0.1, 4, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, lp(2000)], at: 0.14 },
    ],
  },
  /** Bonus fanfare: muted brass A4 D5 F5 → Dm chord hold. */
  bonusStart: {
    gain: 0.2,
    variants: 1,
    jitter: 0,
    maxVoices: 1,
    layers: [
      { p: brass(69, 0.6, 0.06, 0.08), at: 0 },
      { p: brass(74, 0.6, 0.06, 0.08), at: 0.12 },
      { p: brass(77, 0.6, 0.06, 0.08), at: 0.24 },
      { p: brass(74, 0.45, 0.45, 0.4, 2200), at: 0.36 },
      { p: brass(77, 0.4, 0.45, 0.4, 2200), at: 0.36 },
      { p: brass(81, 0.4, 0.45, 0.4, 2200), at: 0.37 },
      { p: [0.8, 0, hz(38), 0.02, 0.4, 0.4, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.36 },
    ],
  },
  /** CRT power-on: 60 Hz hum + faint line whine, then a boot chirp (A5 → A6). */
  wake: {
    gain: 0.2,
    variants: 1,
    jitter: 0.01,
    maxVoices: 1,
    layers: [
      { p: [1, 0, 60, 0.25, 0.5, 0.3, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(600)] },
      { p: [0.07, 0, 5000, 0.25, 0.5, 0.3, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0] },
      { p: [0.8, 0, 880, 0, 0.04, 0.08, 1, 1, 0, 0, 880, 0.04, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.75 },
    ],
  },

  // Act II ----------------------------------------------------------------------
  /** Card flip: a papery snap + soft thump. */
  cardFlip: {
    gain: 0.16,
    minGap: 0.04,
    layers: [
      { p: tick(1, 3000, 0.03, 2500, 0.15) },
      { p: [0.5, 0.05, 160, 0, 0.01, 0.05, 1, 1, 0, 0, 0, 0, 0, 0.2, 0, 0, 0, 1, 0, 0, lp(900)], at: 0.015 },
    ],
  },
  /** Wheel peg tick: short woody click, pitch driven by the caller. */
  wheelTick: {
    gain: 0.12,
    maxVoices: 3,
    minGap: 0.02,
    jitter: 0.02,
    layers: [{ p: [1, 0.05, 700, 0, 0.004, 0.025, 1, 1, 0, 0, 0, 0, 0, 0.3, 0, 0, 0, 1, 0, 0, hp(400)] }],
  },
  /** Devour: sub drop + chomp noise + a reversed-feeling coin swell. */
  devour: {
    gain: 0.32,
    variants: 1,
    maxVoices: 1,
    layers: [
      { p: [1, 0, 70, 0.01, 0.4, 0.5, 1, 1, -0.4, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, lp(500)] },
      { p: [0.9, 0.1, 300, 0, 0.05, 0.2, 4, 1, 0, 0, 0, 0, 0, 2, 0, 0, 0, 1, 0, 0, lp(1800)], at: 0.25 },
      { p: [0.4, 0, hz(93), 0.5, 0.05, 0.02, 0, 1, -0.3, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], at: 0.35 },
    ],
  },
  /** Static scream (Molt, glitches): bitcrushed noise with a falling whine. */
  static: {
    gain: 0.2,
    variants: 2,
    maxVoices: 1,
    layers: [
      { p: [1, 0.2, 800, 0.05, 0.6, 0.4, 4, 1, 0, 0, 0, 0, 0, 3, 0, 0.3, 0, 1, 0, 0, 0] },
      { p: [0.25, 0, 2400, 0.05, 0.5, 0.4, 2, 1, -2, 0, 0, 0, 0, 0, 0, 0.2, 0, 1, 0, 0, 0] },
    ],
  },
  /** Old payphone bell: two ring bursts. */
  phone: {
    gain: 0.18,
    variants: 1,
    maxVoices: 1,
    layers: [
      { p: [1, 0, 1500, 0, 0.35, 0.05, 0, 1, 0, 0, 0, 0, 0.03, 0, 20, 0, 0, 1, 0, 0.9, 0] },
      { p: [1, 0, 1500, 0, 0.35, 0.05, 0, 1, 0, 0, 0, 0, 0.03, 0, 20, 0, 0, 1, 0, 0.9, 0], at: 0.55 },
    ],
  },
  /** Relic gained: a soft minor chord bloom (choir-ish). */
  relic: {
    gain: 0.18,
    variants: 1,
    jitter: 0,
    layers: [
      { p: vibe(69, 0.5, 1.2) },
      { p: vibe(72, 0.45, 1.2), at: 0.05 },
      { p: vibe(76, 0.4, 1.3), at: 0.1 },
      { p: bell(hz(88), 0.25, 0.9), at: 0.18 },
    ],
  },
  /** Signing a deal: a quill scratch, then a rubber stamp. */
  sign: {
    gain: 0.2,
    variants: 2,
    maxVoices: 1,
    layers: [
      { p: [0.5, 0.2, 3000, 0.02, 0.18, 0.05, 4, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, hp(2000)] },
      { p: tick(1, 1200, 0.05, 600), at: 0.3 },
      { p: [0.8, 0.05, 90, 0, 0.03, 0.12, 1, 1, -0.5, 0, 0, 0, 0, 0.4, 0, 0, 0, 1, 0, 0, lp(900)], at: 0.3 },
    ],
  },
  // Endings: rain on the window (soft lowpassed noise that swells and fades).
  rain: {
    gain: 0.16,
    variants: 2,
    maxVoices: 1,
    layers: [
      { p: [1, 0.3, 500, 0.6, 1.4, 0.9, 4, 1, 0, 0, 0, 0, 0, 2, 0, 0, 0, 1, 0, 0, lp(1400)] },
      { p: [0.4, 0.3, 2400, 0.4, 1.2, 0.8, 4, 1, 0, 0, 0, 0, 0.07, 1, 0, 0, 0, 1, 0, 0, hp(1800)], at: 0.2 },
    ],
  },
  // A heavy door: a creak sliding down, then the thud.
  door: {
    gain: 0.22,
    variants: 1,
    maxVoices: 1,
    layers: [
      { p: [0.6, 0, 320, 0.05, 0.45, 0.1, 2, 1.5, -0.8, 0, 0, 0, 0.02, 0.3, 0, 0, 0, 1, 0, 0.3, lp(1600)] },
      { p: [1, 0.05, 60, 0, 0.05, 0.35, 1, 1, -0.3, 0, 0, 0, 0, 0.6, 0, 0, 0, 1, 0, 0, lp(500)], at: 0.62 },
    ],
  },
  // The Cathedral's organ: a minor chord of soft square pipes under a lowpass.
  organ: {
    gain: 0.12,
    variants: 1,
    maxVoices: 2,
    layers: [
      { p: [0.6, 0, hz(45), 0.25, 1.1, 0.9, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0.04, lp(1200)] },
      { p: [0.5, 0, hz(52), 0.3, 1.0, 0.9, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0.05, lp(1200)] },
      { p: [0.45, 0, hz(57), 0.35, 0.9, 0.9, 5, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0.05, lp(1400)] },
      { p: [0.35, 0, hz(60), 0.4, 0.8, 0.9, 2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0.06, lp(1600)] },
    ],
  },
};

/** Timbre tweaks per round-robin variant: [attackScale, releaseScale]. */
const VARIANT_TWEAKS: readonly (readonly [number, number])[] = [
  [1, 1],
  [0.8, 0.88],
  [1.2, 1.12],
];

/**
 * Render one variant of an SFX definition into a mono sample array at `zz.sampleRate`.
 * Pure apart from ZzFX's internal randomness (param 1).
 */
export function buildSfxSamples(def: SfxDef, variant: number, zz: ZzfxApi): Float32Array<ArrayBuffer> {
  const sr = zz.sampleRate;
  const [aScale, rScale] = VARIANT_TWEAKS[variant % VARIANT_TWEAKS.length] ?? [1, 1];
  const rendered: { at: number; s: number[] }[] = [];
  let total = 0;
  for (const layer of def.layers) {
    const p = [...layer.p];
    while (p.length < 21) p.push(undefined);
    p[1] = p[1] ?? 0; // never let ZzFX default its randomness to 0.05
    p[3] = (p[3] ?? 0) * aScale;
    p[5] = (p[5] ?? 0.1) * rScale;
    const s = zz.buildSamples(...p);
    const at = Math.max(0, Math.round((layer.at ?? 0) * sr));
    rendered.push({ at, s });
    total = Math.max(total, at + s.length);
  }
  const out = new Float32Array(Math.max(1, total));
  for (const { at, s } of rendered) {
    for (let i = 0; i < s.length; i++) out[at + i]! += s[i] ?? 0;
  }
  // Guard against NaN (e.g. odd param combos) so one bad sound can't poison the bus.
  for (let i = 0; i < out.length; i++) if (!Number.isFinite(out[i]!)) out[i] = 0;
  return out;
}

// Looping sounds (built from Web Audio nodes, not ZzFX) ------------------------------

export type LoopName = 'whirr' | 'scrape' | 'drone';

export interface LoopHandle {
  stop(): void;
}

const noiseCache = new WeakMap<BaseAudioContext, AudioBuffer>();

/** A shared 2 s mono white-noise buffer per context. */
export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseCache.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    noiseCache.set(ctx, buf);
  }
  return buf;
}

/** Loop levels (into the SFX bus). */
const LOOP_LEVEL: Record<LoopName, number> = { whirr: 0.11, scrape: 0.07, drone: 0.13 };

/**
 * Start a looping sound into `dest`. Returns a handle whose stop() fades it out and
 * releases all nodes.
 */
export function createLoop(name: LoopName, ctx: AudioContext, dest: AudioNode): LoopHandle {
  const t = ctx.currentTime;
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, t);
  out.connect(dest);
  const sources: AudioScheduledSourceNode[] = [];
  const level = LOOP_LEVEL[name];
  let fadeIn = 0.12;

  const noise = (): AudioBufferSourceNode => {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    src.loop = true;
    sources.push(src);
    return src;
  };
  const osc = (type: OscillatorType, freq: number): OscillatorNode => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    sources.push(o);
    return o;
  };

  if (name === 'whirr') {
    // Spinning reels: band-passed noise + low motor saw, chopped by a 13 Hz "cog" AM.
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 1.2;
    const nGain = ctx.createGain();
    nGain.gain.value = 0.7;
    noise().connect(bp).connect(nGain);
    const motorLp = ctx.createBiquadFilter();
    motorLp.type = 'lowpass';
    motorLp.frequency.value = 320;
    const mGain = ctx.createGain();
    mGain.gain.value = 0.5;
    osc('sawtooth', 38).connect(motorLp).connect(mGain);
    const am = ctx.createGain();
    am.gain.value = 0.7;
    const lfo = osc('square', 13);
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.3;
    lfo.connect(lfoDepth).connect(am.gain);
    nGain.connect(am);
    mGain.connect(am);
    am.connect(out);
  } else if (name === 'scrape') {
    // Scratch-off: bright noise with two unsynced LFOs on the gain for a grainy texture.
    const hpF = ctx.createBiquadFilter();
    hpF.type = 'highpass';
    hpF.frequency.value = 1800;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 3500;
    bp.Q.value = 0.8;
    const grain = ctx.createGain();
    grain.gain.value = 0.55;
    noise().connect(hpF).connect(bp).connect(grain);
    for (const [f, depth] of [
      [7, 0.25],
      [11.3, 0.2],
    ] as const) {
      const l = osc('sine', f);
      const g = ctx.createGain();
      g.gain.value = depth;
      l.connect(g).connect(grain.gain);
    }
    grain.connect(out);
    fadeIn = 0.04;
  } else {
    // Tension drone: detuned saws on D, lowpass opening and pitch creeping up ~3 semitones over ~4 s.
    const lpF = ctx.createBiquadFilter();
    lpF.type = 'lowpass';
    lpF.Q.value = 3;
    lpF.frequency.setValueAtTime(200, t);
    lpF.frequency.setTargetAtTime(1400, t, 1.6);
    const mix = ctx.createGain();
    mix.gain.value = 0.5;
    const voices: [OscillatorType, number, number][] = [
      ['sawtooth', 73.42, 0],
      ['sawtooth', 110, 7],
      ['sine', 36.71, 0],
    ];
    for (const [type, f, cents] of voices) {
      const o = osc(type, f);
      o.detune.setValueAtTime(cents, t);
      o.detune.linearRampToValueAtTime(cents + 300, t + 4);
      o.connect(lpF);
    }
    lpF.connect(mix).connect(out);
    fadeIn = 0.4;
  }

  for (const s of sources) s.start(t);
  out.gain.linearRampToValueAtTime(level, t + fadeIn);

  let stopped = false;
  return {
    stop(): void {
      if (stopped) return;
      stopped = true;
      try {
        const now = ctx.currentTime;
        out.gain.cancelScheduledValues(now);
        out.gain.setValueAtTime(out.gain.value, now);
        out.gain.linearRampToValueAtTime(0, now + 0.15);
        for (const s of sources) s.stop(now + 0.2);
        const last = sources[0];
        if (last) last.onended = () => out.disconnect();
      } catch {
        /* context gone */
      }
    },
  };
}
